import {flatten} from 'ramda';

const INDENT = 0;
const DEDENT = 1;
const LINEBREAK = 2;

class Visitor {
    serializeArray(value) {
        const code = flatten([
            ...this.array[0],
            ...value.map(v => this.serialize(v)).map(v => [
                ...v,
                ...this.separator
            ]),
            ...this.array[1]]);

        if (code[-this.array[1].length - 1] === this.separator[-1] &&
            code[-this.array[1].length - 2] === this.separator[-2]
        ) {
            code.splice(-this.array[1].length - 2, 1);
        }

        return code;
    }

    serializeObject(value) {
        const code = flatten([
            ...this.object[0],
            ...Object.entries(value).map(([k, v]) => [
                ...this.key(k),
                ...this.assignment,
                ...this.serialize(v)
            ]).map(v => [
                ...v,
                ...this.separator
            ]),
            ...this.object[1]]);

        if (code[-this.object[1].length - 1] === this.separator[-1] &&
            code[-this.object[1].length - 2] === this.separator[-2]
        ) {
            code.splice(-this.object[1].length - 2, 1);
        }

        return code;
    }

    visit(value) {
        return this.serialize(value);
    }
}

export class JsVisitor extends Visitor {
    constructor() {
        super();
        this.object = [['{', INDENT, LINEBREAK], [DEDENT, '}']];
        this.array = [['[', INDENT, LINEBREAK], [DEDENT, ']']];
        this.assignment = [':'];
        this.separator = [',', LINEBREAK];
    }

    key(key) {
        return [/^[_a-z][a-z0-9_]+$/i.test(key) ? key : `"${key}"`];
    }

    serialize(value) {
        const type = typeof value;
        if (type === 'boolean' || type === 'number' || type === 'string') {
            return [JSON.stringify(value)];
        }
        if (type === 'undefined' || value === null) {
            return ['null'];
        }
        if (Array.isArray(value)) {
            return this.serializeArray(value);
        }
        return this.serializeObject(value);
    }
}

export class PyVisitor extends Visitor {
    constructor() {
        super();
        this.object = [['{', INDENT, LINEBREAK], [DEDENT, '}']];
        this.array = [['[', INDENT, LINEBREAK], [DEDENT, ']']];
        this.assignment = [':'];
        this.separator = [',', LINEBREAK];
    }

    key(key) {
        return [`"${key}"`];
    }

    serialize(value) {
        const type = typeof value;
        if (type === 'number' || type === 'string') {
            return [JSON.stringify(value)];
        }
        if (type === 'boolean') {
            return value ? ['True'] : ['False'];
        }
        if (type === 'undefined' || value === null) {
            return ['None'];
        }
        if (Array.isArray(value)) {
            return this.serializeArray(value);
        }
        return this.serializeObject(value);
    }
}

export class RVisitor extends Visitor {
    constructor() {
        super();
        this.object = [['list(', INDENT, LINEBREAK], [DEDENT, ')']];
        this.array = [['list(', INDENT, LINEBREAK], [DEDENT, ')']];
        this.assignment = ['='];
        this.separator = [',', LINEBREAK];
    }

    key(key) {
        return [`"${key}"`];
    }

    serialize(value) {
        const type = typeof value;
        if (type === 'number' || type === 'string') {
            return [JSON.stringify(value).replace(/'/g, '"')];
        }
        if (type === 'boolean') {
            return value ? ['TRUE'] : ['FALSE'];
        }
        if (type === 'undefined' || value === null) {
            return ['NULL'];
        }
        if (Array.isArray(value)) {
            return this.serializeArray(value);
        }
        return this.serializeObject(value);
    }
}

export const stringify = code => {
    const _code = flatten(code);

    let indent = 0;
    let result = '';

    _code.forEach((e, i) => {
        let prev_i = i - 1;
        let needsIndent = false;

        while (prev_i >= 0) {
            const prev_e = _code[prev_i];
            if (prev_e === LINEBREAK) {
                needsIndent = true;
                break;
            } else if (typeof prev_e === 'string') {
                break;
            }

            prev_i--;
        }

        if (e === INDENT) {
            indent++;
        } else if (e === DEDENT) {
            indent--;
        } else if (e === LINEBREAK) {
            result += '\n';
        } else {
            // n + 1 elements joined by n delimiters (separators) = n spaces
            result += (needsIndent ? new Array(indent * 4 + 1).join(' ') : '') + e;
        }
    });

    return result;
};
